import { Cohort, CohortEnumSchema, ProducerUserTypesEnumSchema, } from '@eigtech/auth0-types';
import { z } from 'zod';
import { getCanonPortalZone, getCohortByPortalZone } from '../util/portalUtils.js';
import { DeprecatedPortalZoneSchema, PortalZoneSchema } from './zones.js';
export const PortalStatusSchema = z.enum([
    'ACTIVE',
    'ACTIVATING',
    // 'EXPIRED',
    'INACTIVE',
    'DEACTIVATING',
    'STAGED',
    'STAGING',
]);
export const portalStatus = PortalStatusSchema.enum;
export const PortalEntityTypeSchema = z.enum(['claim', 'job']);
export const PortalEntityType = PortalEntityTypeSchema.enum;
export const PortalDeactivatedCodeEnumSchema = z.enum([
    'ENTITY_CLOSED',
    'MANUAL',
    'UNASSIGNED',
    'UNKNOWN',
    'USER_MERGED',
]);
export const PortalDeactivatedCode = PortalDeactivatedCodeEnumSchema.enum;
export const PortalProducerSchema = z.object({
    auth0Id: z.string(),
    createdBy: z.string(),
    dateCreated: z.string().datetime(),
    dateModified: z.string().datetime(),
    deactivatedCode: PortalDeactivatedCodeEnumSchema.optional(),
    deactivatedReason: z.string().optional(),
    email: z.string(),
    entityId: z.string(),
    entityPermissions: z.string().array(),
    entityType: PortalEntityTypeSchema,
    externalUserId: z.string(),
    modifiedBy: z.string(),
    phoneNumber: z.string().optional(),
    portalUserId: z.string(),
    status: PortalStatusSchema,
    zone: PortalZoneSchema,
    userType: ProducerUserTypesEnumSchema,
});
// DO NOT USE.  This mapping is only for historical events.
const cohortToUserTypeMap = {
    [Cohort.csr]: 'coordinator',
    [Cohort.estimator]: 'estimator',
    [Cohort.insured]: 'insured',
    [Cohort.super]: 'csrAdmin',
};
export const PortalConsumerInSchema = PortalProducerSchema.or(PortalProducerSchema.omit({ userType: true }).merge(z.object({
    zone: z.enum([...PortalZoneSchema.options, ...DeprecatedPortalZoneSchema.options]),
})));
export const PortalConsumerSchema = PortalConsumerInSchema.transform((portal, ctx) => {
    if (DeprecatedPortalZoneSchema.options.some((zone) => zone === portal.zone)) {
        portal.zone = getCanonPortalZone(portal.zone);
    }
    if ('userType' in portal) {
        // all good
        return portal;
    }
    else {
        const cohort = getCohortByPortalZone(portal.zone);
        const userType = cohortToUserTypeMap[cohort];
        if (userType) {
            return {
                ...portal,
                userType,
            };
        }
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `User type is required`,
        path: ['userType'],
    });
    return z.NEVER;
});
export const StagedPortalProducerSchema = PortalProducerSchema.omit({
    auth0Id: true,
    email: true,
    portalUserId: true,
}).merge(z.object({
    inactive: z.boolean(),
}));
export const StagedPortalConsumerInSchema = StagedPortalProducerSchema.or(StagedPortalProducerSchema.omit({ userType: true }).merge(z.object({ cohort: CohortEnumSchema })));
export const StagedPortalConsumerSchema = StagedPortalConsumerInSchema.transform((portal, ctx) => {
    if ('userType' in portal) {
        // all good
        return portal;
    }
    else if ('cohort' in portal) {
        const { cohort, ...rest } = portal;
        const userType = cohortToUserTypeMap[cohort];
        if (userType) {
            return {
                ...rest,
                userType,
            };
        }
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `User type is required`,
        path: ['userType'],
    });
    return z.NEVER;
});
export const isPortal = (portal) => {
    return PortalConsumerInSchema.safeParse(portal).success;
};
export const isStagedPortal = (portal) => {
    return StagedPortalConsumerInSchema.safeParse(portal).success;
};
export const ensureCompletePortal = (portal) => {
    const parsed = PortalConsumerSchema.safeParse(portal);
    return parsed.success ? parsed.data : undefined;
};
export const ensureStagedPortal = (portal) => {
    const parsed = StagedPortalConsumerSchema.safeParse(portal);
    return parsed.success ? parsed.data : undefined;
};
